// Central API client for YUP
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Helper: resolve image URL (absolute if already full URL, otherwise prefix backend)
export function resolveImage(url) {
  if (!url) return url;
  if (url.startsWith('http') || url.startsWith('data:')) return url;
  if (url.startsWith('/api/')) return `${BACKEND_URL}${url}`;
  return url;
}

const pub = axios.create({ baseURL: `${API}/public`, timeout: 30000 });
const adm = axios.create({ baseURL: `${API}/admin`, timeout: 30000 });
const spn = axios.create({ baseURL: `${API}/sponsor`, timeout: 30000 });

// Attach sponsor JWT to every sponsor request
spn.interceptors.request.use((cfg) => {
  const t = localStorage.getItem('yup_sponsor_token');
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

spn.interceptors.response.use(
  r => r,
  err => {
    if (err.response?.status === 401) {
      localStorage.removeItem('yup_sponsor_token');
      if (window.location.pathname.startsWith('/sponsor') && window.location.pathname !== '/sponsor/login') {
        window.location.href = '/sponsor/login';
      }
    }
    return Promise.reject(err);
  }
);

// Attach admin JWT to every admin request
adm.interceptors.request.use((cfg) => {
  const t = localStorage.getItem('yup_admin_token');
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

adm.interceptors.response.use(
  r => r,
  err => {
    if (err.response?.status === 401 || err.response?.status === 403) {
      localStorage.removeItem('yup_admin_token');
      if (window.location.pathname.startsWith('/admin') && window.location.pathname !== '/admin/login') {
        window.location.href = '/admin/login';
      }
    }
    return Promise.reject(err);
  }
);

// ================= PUBLIC =================
export const publicApi = {
  cities: () => pub.get('/cities').then(r => r.data),
  city:   (slug) => pub.get(`/cities/${slug}`).then(r => r.data),
  zones: () => pub.get('/zones').then(r => r.data),
  zone: (slug) => pub.get(`/zones/${slug}`).then(r => r.data),
  homeTiles: () => pub.get('/home-tiles').then(r => r.data),
  categories: () => pub.get('/categories').then(r => r.data),
  subcategories: (category, city) => pub.get('/subcategories', {
    params: { ...(category ? { category } : {}), ...(city ? { city } : {}) }
  }).then(r => r.data),
  activities: (params = {}) => pub.get('/activities', { params }).then(r => r.data),
  activity: (id) => pub.get(`/activities/${id}`).then(r => r.data),
  homeFeatured: (limit = 8) => pub.get('/home-featured', { params: { limit } }).then(r => r.data),
  search: (q, params = {}) => pub.get('/search', { params: { q, ...params } }).then(r => r.data),
  nearby: (lat, lng, params = {}) => pub.get('/nearby', { params: { lat, lng, ...params } }).then(r => r.data),
  story: (slug) => pub.get(`/stories/${slug}`).then(r => r.data),
  emergency: (slug) => pub.get(`/emergency/${slug}`).then(r => r.data),
  subscribe: (email, lang) => pub.post('/newsletter/subscribe', { email, lang }).then(r => r.data),
  banners: (city) => pub.get('/banners', { params: city ? { city } : {} }).then(r => r.data),
  bannerClick: (id) => pub.post(`/banners/${id}/click`).then(r => r.data).catch(() => null),
  flashOffers: (city) => pub.get('/flash-offers', { params: city ? { city } : {} }).then(r => r.data),
  itineraries: (city) => pub.get('/itineraries', { params: city ? { city } : {} }).then(r => r.data),
  itinerary: (slug) => pub.get(`/itineraries/${slug}`).then(r => r.data),
  experiences: (city) => pub.get('/experiences', { params: city ? { city } : {} }).then(r => r.data),
  experience: (slug) => pub.get(`/experiences/${slug}`).then(r => r.data),
  submitPartnerLead: (data) => pub.post('/partner-leads', data).then(r => r.data),
  qrPage: (slug) => pub.get(`/qr-pages/${slug}`).then(r => r.data),
  settings: () => pub.get('/settings').then(r => r.data),
  currencyRates: () => pub.get('/currency-rates').then(r => r.data),
  track: (event, extra = {}) => pub.post('/track', { event, ...extra }).catch(() => null),
  chat: (session_id, message, lang) => pub.post('/chat', { session_id, message, lang }).then(r => r.data),
  chatHistory: (session_id) => pub.get(`/chat/${session_id}`).then(r => r.data)
};

// ================= ADMIN =================
export const adminApi = {
  login: (email, password) => adm.post('/auth/login', { email, password }).then(r => r.data),
  me: () => adm.get('/auth/me').then(r => r.data),
  stats: () => adm.get('/stats').then(r => r.data),

  cities: () => adm.get('/cities').then(r => r.data),
  createCity: (data) => adm.post('/cities', data).then(r => r.data),
  updateCity: (id, data) => adm.put(`/cities/${id}`, data).then(r => r.data),
  deleteCity: (id) => adm.delete(`/cities/${id}`).then(r => r.data),

  zones: () => adm.get('/zones').then(r => r.data),
  createZone: (data) => adm.post('/zones', data).then(r => r.data),
  updateZone: (id, data) => adm.put(`/zones/${id}`, data).then(r => r.data),
  deleteZone: (id) => adm.delete(`/zones/${id}`).then(r => r.data),
  retranslateZone: (id) => adm.post(`/zones/${id}/retranslate`).then(r => r.data),

  // ---- File upload (returns { url }) ----
  upload: (file, onProgress) => {
    const fd = new FormData(); fd.append('file', file);
    return adm.post('/upload', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (evt) => onProgress && evt.total && onProgress(Math.round((evt.loaded / evt.total) * 100))
    }).then(r => r.data);
  },

  categories: () => adm.get('/categories').then(r => r.data),
  createCategory: (data) => adm.post('/categories', data).then(r => r.data),
  updateCategory: (id, data) => adm.put(`/categories/${id}`, data).then(r => r.data),
  deleteCategory: (id) => adm.delete(`/categories/${id}`).then(r => r.data),
  toggleCategoryVisibility: (id) => adm.patch(`/categories/${id}/visibility`).then(r => r.data),
  reorderCategories: (order) => adm.post('/categories/reorder', { order }).then(r => r.data),

  subcategories: () => adm.get('/subcategories').then(r => r.data),
  createSubcategory: (data) => adm.post('/subcategories', data).then(r => r.data),
  updateSubcategory: (id, data) => adm.put(`/subcategories/${id}`, data).then(r => r.data),
  deleteSubcategory: (id) => adm.delete(`/subcategories/${id}`).then(r => r.data),
  toggleSubcategoryVisibility: (id) => adm.patch(`/subcategories/${id}/visibility`).then(r => r.data),
  reorderSubcategories: (order) => adm.post('/subcategories/reorder', { order }).then(r => r.data),

  activities: () => adm.get('/activities').then(r => r.data),
  createActivity: (data) => adm.post('/activities', data).then(r => r.data),
  updateActivity: (id, data) => adm.put(`/activities/${id}`, data).then(r => r.data),
  deleteActivity: (id) => adm.delete(`/activities/${id}`).then(r => r.data),
  toggleActivityVisibility: (id) => adm.patch(`/activities/${id}/visibility`).then(r => r.data),
  bulkStatus: (ids, status) => adm.post('/activities/bulk-status', { ids, status }).then(r => r.data),
  toggleActivityHomeFeatured: (id) => adm.patch(`/activities/${id}/home-featured`).then(r => r.data),
  reseedSubcategories: () => adm.post('/reseed-subcategories').then(r => r.data),
  reorderActivities: (order) => adm.post('/activities/reorder', { order }).then(r => r.data),
  bannerStats: (id, days = 14) => adm.get(`/banners/${id}/stats`, { params: { days } }).then(r => r.data),
  moderationLogs: (blockedOnly = false) => adm.get('/moderation-logs', { params: { blocked_only: blockedOnly } }).then(r => r.data),
  deleteModerationLog: (id) => adm.delete(`/moderation-logs/${id}`).then(r => r.data),

  // ---- Broken images ----
  brokenImages: () => adm.get('/broken-images').then(r => r.data),
  clearBrokenImages: () => adm.post('/broken-images/clear').then(r => r.data),

  // ---- Quick widgets (partner tiles managed globally) ----
  listQuickWidgets: () => adm.get('/quick-widgets').then(r => r.data),
  applyQuickWidget: (key, citySlugs) => adm.post(`/quick-widgets/${key}/apply`, { city_slugs: citySlugs }).then(r => r.data),

  // ---- Google Places ----
  googleSearch: (actId, query) => adm.post(`/activities/${actId}/google/search`, { query }).then(r => r.data),
  googleLink: (actId, place_id) => adm.post(`/activities/${actId}/google/link`, { place_id }).then(r => r.data),
  googleRefresh: (actId, force = false) => adm.post(`/activities/${actId}/google/refresh${force ? '?force=true' : ''}`).then(r => r.data),
  googleUnlink: (actId) => adm.post(`/activities/${actId}/google/unlink`).then(r => r.data),
  googleBulkEnrich: ({ limit, city } = {}) => {
    const params = new URLSearchParams();
    if (limit) params.set('limit', String(limit));
    if (city) params.set('city', city);
    const qs = params.toString();
    return adm.post(`/activities/google/bulk-enrich${qs ? '?' + qs : ''}`).then(r => r.data);
  },
  googleBulkStatus: (jobId) => adm.get(`/activities/google/bulk-enrich/${jobId}`).then(r => r.data),
  googleDiscover: (payload) => adm.post('/activities/google/discover', payload).then(r => r.data),
  googleBulkImport: (payload) => adm.post('/activities/google/bulk-import', payload).then(r => r.data),

  emergency: () => adm.get('/emergency').then(r => r.data),
  createEmergency: (data) => adm.post('/emergency', data).then(r => r.data),
  updateEmergency: (id, data) => adm.put(`/emergency/${id}`, data).then(r => r.data),
  deleteEmergency: (id) => adm.delete(`/emergency/${id}`).then(r => r.data),
  emergencySyncCountry: (slug, mode) => adm.post(`/emergency/sync-country/${slug}`, { mode }).then(r => r.data),
  emergencyGetLocalTemplates: () => adm.get('/emergency-local-templates').then(r => r.data),
  emergencySaveLocalTemplates: (templates) => adm.put('/emergency-local-templates', { templates }).then(r => r.data),
  emergencyApplyLocalTemplates: (slug) => adm.post(`/emergency/apply-local-templates/${slug}`).then(r => r.data),

  stories: () => adm.get('/stories').then(r => r.data),
  upsertStory: (slug, data) => adm.put(`/stories/${slug}`, data).then(r => r.data),

  newsletter: () => adm.get('/newsletter').then(r => r.data),

  // ---- Import ----
  importJobs: () => adm.get('/import/jobs').then(r => r.data),
  wixScan: () => adm.post('/import/wix/scan').then(r => r.data),
  wixStatus: (jobId) => adm.get(`/import/wix/status/${jobId}`).then(r => r.data),
  wixApply: (jobId) => adm.post(`/import/wix/apply/${jobId}`).then(r => r.data),
  csvTemplate: () => adm.get('/import/csv/template').then(r => r.data),
  csvPreview: (file) => {
    const fd = new FormData(); fd.append('file', file);
    return adm.post('/import/csv/preview', fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
  },
  csvApply: (file) => {
    const fd = new FormData(); fd.append('file', file);
    return adm.post('/import/csv/apply', fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
  },
  csvApplyAsync: (file, onUploadProgress) => {
    const fd = new FormData(); fd.append('file', file);
    return adm.post('/import/csv/apply-async', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (evt) => {
        if (onUploadProgress && evt.total) onUploadProgress(Math.round((evt.loaded / evt.total) * 100));
      }
    }).then(r => r.data);
  },
  csvJobStatus: (jobId) => adm.get(`/import/csv/job/${jobId}`).then(r => r.data),
  wixCsvApply: (files) => {
    const fd = new FormData();
    files.forEach(f => fd.append('files', f));
    return adm.post('/import/wix-csv/apply', fd, { headers: { 'Content-Type': 'multipart/form-data' } }).then(r => r.data);
  },
  csvExport: (params = {}) => adm.get('/import/csv/export', { params }).then(r => r.data),

  // ---- Banners ----
  banners: () => adm.get('/banners').then(r => r.data),
  createBanner: (data) => adm.post('/banners', data).then(r => r.data),
  updateBanner: (id, data) => adm.put(`/banners/${id}`, data).then(r => r.data),
  deleteBanner: (id) => adm.delete(`/banners/${id}`).then(r => r.data),

  // ---- Flash Offers ----
  offers: () => adm.get('/flash-offers').then(r => r.data),
  createOffer: (data) => adm.post('/flash-offers', data).then(r => r.data),
  updateOffer: (id, data) => adm.put(`/flash-offers/${id}`, data).then(r => r.data),
  deleteOffer: (id) => adm.delete(`/flash-offers/${id}`).then(r => r.data),

  // ---- Itineraries ----
  itineraries: () => adm.get('/itineraries').then(r => r.data),
  createItinerary: (data) => adm.post('/itineraries', data).then(r => r.data),
  updateItinerary: (id, data) => adm.put(`/itineraries/${id}`, data).then(r => r.data),
  deleteItinerary: (id) => adm.delete(`/itineraries/${id}`).then(r => r.data),
  generateItinerary: (data) => adm.post('/itineraries/generate', data).then(r => r.data),

  // ---- Experiences ----
  experiences: () => adm.get('/experiences').then(r => r.data),
  createExperience: (data) => adm.post('/experiences', data).then(r => r.data),
  updateExperience: (id, data) => adm.put(`/experiences/${id}`, data).then(r => r.data),
  deleteExperience: (id) => adm.delete(`/experiences/${id}`).then(r => r.data),

  // ---- Partner leads ----
  partnerLeads: () => adm.get('/partner-leads').then(r => r.data),
  updatePartnerLead: (id, data) => adm.patch(`/partner-leads/${id}`, data).then(r => r.data),
  deletePartnerLead: (id) => adm.delete(`/partner-leads/${id}`).then(r => r.data),

  // ---- QR Freeversion landing pages ----
  qrPages: () => adm.get('/qr-pages').then(r => r.data),
  createQrPage: (data) => adm.post('/qr-pages', data).then(r => r.data),
  updateQrPage: (id, data) => adm.put(`/qr-pages/${id}`, data).then(r => r.data),
  deleteQrPage: (id) => adm.delete(`/qr-pages/${id}`).then(r => r.data),

  // ---- App settings (partner logos, feature flags, ...) ----
  getSettings: () => adm.get('/settings').then(r => r.data),
  setSetting: (key, value) => adm.put('/settings', { key, value }).then(r => r.data),

  // ---- Notifications ----
  notifications: (status) => adm.get('/notifications', { params: status ? { status } : {} }).then(r => r.data),
  markNotificationSent: (id) => adm.post(`/notifications/${id}/mark-sent`).then(r => r.data),
  deleteNotification: (id) => adm.delete(`/notifications/${id}`).then(r => r.data),

  // ---- Experience AI classify ----
  classifyExperience: (data) => adm.post('/experiences/classify', data).then(r => r.data),

  // ---- Analytics ----
  analyticsSummary: (days = 30) => adm.get('/analytics/summary', { params: { days } }).then(r => r.data),
  analyticsCsvUrl: (days = 30) => `${API}/admin/analytics/csv?days=${days}`,

  // ---- Translation ----
  translateActivity: (text, source_lang, target_lang) => adm.post('/translate-activity', { text, source_lang, target_lang }).then(r => r.data),

  // ---- Geocoding ----
  geocodeRun: (limit = 200) => adm.post('/import/geocode/run', null, { params: { limit } }).then(r => r.data),
  geocodeStatus: (jobId) => adm.get(`/import/geocode/status/${jobId}`).then(r => r.data),
  geocodeOne: (address) => adm.get('/import/geocode/one', { params: { address } }).then(r => r.data),

  // ---- Sponsor users ----
  sponsorUsers: () => adm.get('/sponsor-users').then(r => r.data),
  createSponsorUser: (data) => adm.post('/sponsor-users', data).then(r => r.data),
  updateSponsorUser: (id, data) => adm.patch(`/sponsor-users/${id}`, data).then(r => r.data),
  deleteSponsorUser: (id) => adm.delete(`/sponsor-users/${id}`).then(r => r.data),

  // ---- Sponsor upgrades ----
  sponsorUpgrades: (status) => adm.get('/sponsor-upgrades', { params: status ? { status } : {} }).then(r => r.data),
  updateSponsorUpgrade: (id, data) => adm.patch(`/sponsor-upgrades/${id}`, data).then(r => r.data),
  deleteSponsorUpgrade: (id) => adm.delete(`/sponsor-upgrades/${id}`).then(r => r.data),

  // ---- Currency ----
  currencyRates: () => adm.get('/currency-rates').then(r => r.data),
  refreshCurrencyRates: () => adm.post('/currency-rates/refresh').then(r => r.data),
  setCurrencyRates: (rates, manual_override = true) => adm.put('/currency-rates', { rates, manual_override }).then(r => r.data),
};

// ================= SPONSOR PORTAL =================
export const sponsorApi = {
  login: (email, password) => spn.post('/auth/login', { email, password }).then(r => {
    localStorage.setItem('yup_sponsor_token', r.data.access_token);
    return r.data;
  }),
  me: () => spn.get('/me').then(r => r.data),
  myActivities: () => spn.get('/activities').then(r => r.data),
  updateActivity: (id, data) => spn.patch(`/activities/${id}`, data).then(r => r.data),
  myOffers: () => spn.get('/flash-offers').then(r => r.data),
  createOffer: (data) => spn.post('/flash-offers', data).then(r => r.data),
  updateOffer: (id, data) => spn.patch(`/flash-offers/${id}`, data).then(r => r.data),
  deleteOffer: (id) => spn.delete(`/flash-offers/${id}`).then(r => r.data),
  analytics: (days = 30) => spn.get('/analytics', { params: { days } }).then(r => r.data),
  demoReset: () => spn.post('/demo/reset').then(r => r.data),
  upload: (file, onProgress) => {
    const fd = new FormData(); fd.append('file', file);
    return spn.post('/upload', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (evt) => onProgress && evt.total && onProgress(Math.round((evt.loaded / evt.total) * 100))
    }).then(r => r.data);
  },
  listUpgrades: () => spn.get('/upgrades').then(r => r.data),
  createUpgrade: (data) => spn.post('/upgrades', data).then(r => r.data),
  cancelUpgrade: (id) => spn.delete(`/upgrades/${id}`).then(r => r.data),
};

export function isSponsorAuthed() {
  return !!localStorage.getItem('yup_sponsor_token');
}
export function sponsorLogout() {
  localStorage.removeItem('yup_sponsor_token');
}

export function isAdminAuthed() {
  return !!localStorage.getItem('yup_admin_token');
}

export function adminLogout() {
  localStorage.removeItem('yup_admin_token');
}
